import { GroupSelectItem } from '../../components/Chat/GroupSelectItem';
import { useRef } from 'react';
import type { Group } from '../GroupsList/groupsSlice/types';
import { useAppDispatch, useAppSelector } from '../../hooks';
import Loader from '../../components/Loader/Loader';
import { handleSelectGroup, isGroupsLoading, selectGroupsSelection } from './checklistsSlice';
import { checkGroupsType } from '../CreateMessage/helpers';
import { SList } from '../CreateMessage/SelectGroupsList';
import { EmptyListFallback } from '../../components/EmptyListFallback/EmptyListFallback';
import styled from 'styled-components';
import { useTenantId } from '../../utils/customHooks/useTenantId';
import { HighlightSearchTerm } from '../../components/Checklists/HighlightSearchTerm/HighlightSearchTerm';
import type { GroupMember } from '../GroupDetail/groupDetailSlice/types';
import { fetchAllGroupMembers } from '../../api/groups';

type ChecklistSelectGroupsListProps = {
  searchText?: string;
  groups: Group[];
  isActive: boolean;
};

const SEmptyListFallback = styled(EmptyListFallback)`
  p {
    font-family: 'Roboto-Regular';
    font-weight: 400;
  }
`;

export const SCustomList = styled(SList)`
  padding: 1.25rem 0 5rem 0;
`;

export const ChecklistSelectGroupsList = (props: ChecklistSelectGroupsListProps) => {
  const { searchText, groups, isActive } = props;
  const dispatch = useAppDispatch();
  const tenantId = useTenantId();

  const { isGroupsSelected } = useAppSelector(selectGroupsSelection);
  const isLoading = useAppSelector(isGroupsLoading);
  const usersListRef = useRef<HTMLDivElement>(null);

  const onGroupClick = async (group: Group) => {
    const { id, membersCount } = group;

    let groupMembers = [] as GroupMember[];
    if (membersCount > 1) {
      if (!tenantId) {
        throw new Error('Tenant missing');
      }
      groupMembers = await fetchAllGroupMembers({ groupId: id, tenantId });
    }

    dispatch(
      handleSelectGroup({ groupId: id, usersIds: groupMembers.map(member => member.userID) })
    );
  };

  if (isLoading && !groups.length) return <Loader />;

  if (!isActive) return <></>;

  return (
    <SCustomList ref={usersListRef}>
      {groups.map((group, index) => (
        <GroupSelectItem
          id={`group-${index + 1}`}
          key={group.id}
          name={HighlightSearchTerm(group.name, searchText)}
          membersCount={group.membersCount}
          type={checkGroupsType(group.groupType)}
          isSelected={isGroupsSelected(group.id)}
          photoFileName={group.imageFileName || undefined}
          onCardPress={() => onGroupClick(group)}
        />
      ))}
      {!groups.length && (
        <SEmptyListFallback
          listLength={groups.length}
          isLoading={false}
          emptyListTx={'checklist_share_empty'}
        />
      )}
    </SCustomList>
  );
};
